import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { Button, TableCell, TableRow, TableContainer, Table, TableHead, TableBody, Dialog, DialogActions, FormControlLabel, Checkbox, FormGroup, DialogContent, DialogTitle } from '@mui/material';
import { UserContext } from '../../../../components/shared/useUser';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { CountZoneDto } from '../../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import useLogError from '../../../../hooks/useLogError';
import { StockCountCreateContext, StockCountCreateValues } from '../../contexts/StockCountCreateContext';
import { CloseDialogAction } from '../../../../components/shared/dialogs/CloseDialogAction';

const StockCountCreateCountZonesModal = () => {
  const { isCountZonesModalOpen,
    handleClose,
    countZonesData,
    handleCountZones } = useContext(StockCountCreateContext) as StockCountCreateValues;

  const { selectedStore } = useContext(UserContext);
  const { getAvailableCountZones } = useNswagClient();
  const [data, setData] = useState<CountZoneDto[] | undefined>([]);
  const [tempCountZoneData, setTempCountZoneData] = useState<CountZoneDto[] | undefined>([]);
  const { t } = useTranslation('createStockCount');
  const { logError } = useLogError();

  const handleConfirm = () => {
    handleCountZones(tempCountZoneData || []);
    setTempCountZoneData([]);
  };

  const handleCountZoneCheckboxChange = (countZoneId: number, isChecked: boolean) => {
    if (isChecked) {
      const selected = data?.find(row => row.id === countZoneId);
      if (selected && !tempCountZoneData?.some(z => z.id === countZoneId)) {
        setTempCountZoneData(prev => [...(prev ?? []), selected]);
      }
    } else {
      setTempCountZoneData(prev => (prev ?? []).filter(row => row.id !== countZoneId));
    }
  };


  const handleSelectAllChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setTempCountZoneData(checked ? data ?? [] : []);
  };

  useEffect(() => {
    if (isCountZonesModalOpen) {
      setTempCountZoneData(countZonesData ?? []);
    }
    getData();
  }, [isCountZonesModalOpen]);

  const getData = () => {
    const storeNumber = selectedStore?.storeNumber === undefined ? '' : selectedStore.storeNumber;
    getAvailableCountZones([storeNumber]).then((result) => {
      if (result) {
        const countZones = result.countZones ?? new Array<CountZoneDto>();
        countZones.sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0);
        setData(countZones);
      } else {
        logError(result);
      }
    })
      .catch((error) => {
        logError(error);
      });
  };

  return (
    <Dialog open={isCountZonesModalOpen}
      onClose={handleClose}
      maxWidth='md'
      fullWidth>
      <DialogTitle>
        {t('countZones.title')}
        <CloseDialogAction onCancel={handleClose} />
      </DialogTitle>
      <DialogContent>
        <TableContainer sx={{ overflowy: 'auto', border: '1px solid' }}
          style={{ maxHeight: '400px' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell >{t('countZones.name')}</TableCell>
                <TableCell >{t('countZones.numberOfItems')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.map((row) => (
                <TableRow key={row.id}>
                  <TableCell style={{ display: 'flex', alignItems: 'center' }}  >
                    <Checkbox
                      color="primary"
                      inputProps={{
                        'aria-label': `select category ${row.name}`,
                      }}
                      checked={tempCountZoneData?.some(item => item.id === row.id)}
                      onChange={(e) => handleCountZoneCheckboxChange(row.id!, e.target.checked)} />
                    {row.name}
                  </TableCell>
                  <TableCell >{row.numberOfItems}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <FormGroup sx={{ pt: '10px' }}>
          <FormControlLabel
            control={<Checkbox onChange={handleSelectAllChange} />}
            label={t('countZones.selectAll')} />
        </FormGroup>
      </DialogContent>
      <DialogActions >
        <Button
          variant="secondary"
          size='lg'
          onClick={handleClose}> {t('cancel')}</Button>
        <Button
          size='lg'
          onClick={handleConfirm}>
          {t('confirm')}
        </Button>
      </DialogActions>
    </Dialog>

  );
};

export default StockCountCreateCountZonesModal;